/** Copyright (c) 2022-present TVM Engineering Authors. */
/* eslint-disable */
import React, { Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import LoadingScreen from "./components/feedback/LoadingScreen";
import STATIC_ROUTES from "./route/STATIC_ROUTES";
// Import Higher Order Components
import { RouteGuard } from "./components/auth/RouteGuard";
import { PermissionsSchema } from "./components/auth/PermissionsWrapper";
import ErrorBoundary from "./route/ErrorBoundary";
import { NotFoundTypes } from "./pages/StaticPages/NotFoundPage";

// Index - Open Routes
import HomeLayout from "./components/layout/HomeLayout/HomeLayout";
import LandingPage from "./pages/LandingPage/LandingPage";
import SignUpPage from "./pages/SignUp/SingUpPage";
import Logout from "./containers/auth/Logout";
import ConfirmRegistration from "./containers/auth/ConfirmRegistration";
import NotFoundPage from "./pages/StaticPages/NotFoundPage";
import ForgotPasswordPage from "./containers/auth/ForgotPassword";
import ResetPassword from "./containers/auth/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/StaticPages/TermsGeneralPage";
import ImprintPage from "./pages/ImprintPage";
import ShareDownloadLink from "./pages/ShareDownloadLink/ShareDownloadLink";
import RegistrationForward from "./containers/auth/RegistrationForward";

// Dashboard - Auth Routes
import DashboardLayout from "./components/dashboard/layout/dashboardLayout";
import DashboardHomePage from "./pages/Dashboard/DashboardHomepage";
import ProjectsLayout from "./components/dashboard/layout/ProjectsLayout/ProjectsLayout";
import DashboardProjects from "./pages/Dashboard/Projects/Project";
import ProjectsById from "./pages/Dashboard/ProjectById/ProjectById";
// import CompaniesPage from "./pages/Dashboard/Companies/Companies";
// import UsersPage from "./pages/Dashboard/Users/Users";
import UserProfilePage from "./pages/Dashboard/UserProfile/UserProfile";
import TermsPage from "./pages/Dashboard/Terms/Terms";
import AccountSettings from "./pages/Dashboard/AccountSettings/AccountSettings";
import TestPage from "./pages/Dashboard/TestPage/TestPage";
import AddNews from "./pages/Dashboard/News/AddNews";
import FileDetails from "./pages/Dashboard/FileManagement/FileDetails/FileDetails";
import SingleNews from "./pages/Dashboard/News/SingleNews";
import FileCatalogPage from "./pages/Dashboard/FileCatalog/FileCatalog";
import MyFilesPage from "./pages/Dashboard/MyFiles/MyFiles";
// import DownloadHistory from "./pages/Dashboard/DownloadHistory/DownloadHistory";
// import AvailableCompany from "./components/company/AvailableCompany/AvailableCompany";

const FileManagementPage = React.lazy(() =>
  import("./pages/Dashboard/FileManagement/FileManagement")
);
const UsersPage = React.lazy(() => import("./pages/Dashboard/Users/Users"));
const RoleManagementPage = React.lazy(() =>
  import("./pages/Dashboard/RoleManagement/RoleManagement")
);
const CompanyPage = React.lazy(() => import("./pages/Dashboard/Companies/Companies"));
const FileRequestsPage = React.lazy(() => import("./pages/Dashboard/FileRequests/FileRequests"));
const AvailableCompany = React.lazy(() => import("./components/company/AvailableCompany/AvailableCompany"));
const DownloadHistory = React.lazy(() => import("./pages/Dashboard/DownloadHistory/DownloadHistory"));
// const FileCatalogPage = React.lazy(() => import("./pages/Dashboard/FileCatalog/FileCatalog"));
// import RoleManagementPage from "./pages/Dashboard/RoleManagement/RoleManagement";

// const ProfilePage = React.lazy(() => import("./pages/ProfilePage/ProfilePage"));
// const TermsAndConditionsPage = React.lazy(() =>
//   import("./pages/TermsAndConditionsPage/TermsAndConditionsPage")
// );
// const ProjectsTablePage = React.lazy(() => import("./pages/ProjectTablePage"));
// const AdminDashboard = React.lazy(() => import("./pages/AdminPage/AdminDashboard"));
// const NotFoundPage = React.lazy(() => import("./pages/StaticPages/NotFoundPage"));
// // const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
// const SupportPage = React.lazy(() => import("./pages/SupportPage"));
// const UploadsPage = React.lazy(() => import("./pages/UploadsPage/UploadsPage"));
// const DownloadHistoryPage = React.lazy(() => import("./pages/DownloadHistoryPage/DownloadHistory"));
// const Maintenance = React.lazy(() => import("./pages/Maintenance/Maintenance"));

// const NoUser = React.lazy(() => import("./components/NoUser"));

// const ROLES = { user: "user", super_admin: "super-admin", admin: "admin", basic: "Basic - admin" };

const AppRoutes = ({ user }) => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/" element={<HomeLayout />}>
              <Route index path={STATIC_ROUTES.landingPage} element={<LandingPage />} />
              <Route path={STATIC_ROUTES.signup} element={<SignUpPage />} />
              <Route path={STATIC_ROUTES.logout} element={<LandingPage />} />
              {/* Removed logout - now directly we go to the landing page */}
              {/* <Route path={STATIC_ROUTES.logout} element={<Logout />} /> */}
              {/* <Route path={STATIC_ROUTES.confirmRegistration} element={<ConfirmRegistration />} /> */}
              <Route path={STATIC_ROUTES.confirmRegistration} element={<LandingPage />} />
              {/* <Route path={STATIC_ROUTES.registrationForward} element={<RegistrationForward />} /> */}
              <Route path={STATIC_ROUTES.registrationForward} element={<SignUpPage />} />
              <Route path={STATIC_ROUTES.forgotPassword} element={<ForgotPasswordPage />} />
              <Route path={STATIC_ROUTES.resetPassword} element={<ResetPassword />} />
              <Route path={STATIC_ROUTES.privacyPolicy} element={<PrivacyPolicy />} />
              <Route path={STATIC_ROUTES.termsOfUse} element={<TermsOfUse />} />
              <Route path={STATIC_ROUTES.imprint} element={<ImprintPage />} />
              <Route path={STATIC_ROUTES.shareDownloadLink} element={<ShareDownloadLink />} />
              <Route path="*" element={<NotFoundPage type={NotFoundTypes.index} />} />
            </Route>
            <Route
              path="/dashboard"
              element={
                user?.token && user?.user?.id ? (
                  <DashboardLayout />
                ) : (
                  <Navigate to={STATIC_ROUTES.landingPage} />
                )
              }
            >
              <Route index path={STATIC_ROUTES.dashboardHome} element={<DashboardHomePage />} />
              <Route
                path={STATIC_ROUTES.companies}
                element={RouteGuard(CompanyPage, [
                  PermissionsSchema.companies.create_company,
                  PermissionsSchema.companies.delete_company,
                  PermissionsSchema.companies.add_members,
                  PermissionsSchema.companies.add_team,
                  PermissionsSchema.companies.delete_team,
                  PermissionsSchema.companies.edit_company,
                  PermissionsSchema.companies.edit_team_name,
                  PermissionsSchema.companies.remove_users,
                  PermissionsSchema.companies.view_all_companies,
                  ,
                ])}
              />
              <Route
                path={STATIC_ROUTES.companyById}
                element={RouteGuard(AvailableCompany, [
                  PermissionsSchema.companies.create_company,
                  PermissionsSchema.companies.delete_company,
                  PermissionsSchema.companies.add_members,
                  PermissionsSchema.companies.add_team,
                  PermissionsSchema.companies.delete_team,
                  PermissionsSchema.companies.edit_company,
                  PermissionsSchema.companies.edit_team_name,
                  PermissionsSchema.companies.remove_users,
                  PermissionsSchema.companies.view_all_companies,
                  ,
                ])} />
              <Route
                path={STATIC_ROUTES.users}
                element={RouteGuard(UsersPage, [
                  PermissionsSchema.users.activate,
                  PermissionsSchema.users.deactivate,
                  PermissionsSchema.users.delete,
                  PermissionsSchema.users.create,
                  PermissionsSchema.users.update,
                ])}
              />
              <Route path={STATIC_ROUTES.userProfile} element={<UserProfilePage />} />
              <Route path={STATIC_ROUTES.terms} element={<TermsPage />} />
              <Route
                path={STATIC_ROUTES.fileManagement}
                element={RouteGuard(FileManagementPage, [PermissionsSchema.files.fm_access])}
              />
              <Route
                path={STATIC_ROUTES.fileRequests}
                element={RouteGuard(FileRequestsPage, [
                  PermissionsSchema.files.file_requests,
                  PermissionsSchema.files.all_file_requests,
                ])}
              />
              <Route path={STATIC_ROUTES.downloadHistory} element={RouteGuard(DownloadHistory, [
                PermissionsSchema.files.view_download_history,
              ])} />
              <Route path={STATIC_ROUTES.fileId} element={<FileDetails />} />
              <Route
                path={STATIC_ROUTES.roleManagement}
                element={RouteGuard(RoleManagementPage, [
                  PermissionsSchema.roles.create,
                  PermissionsSchema.roles.update,
                  PermissionsSchema.roles.delete,
                  PermissionsSchema.roles.copy,
                ])}
              />
              {/* <Route
                path={STATIC_ROUTES.fileCatalog}
                element={RouteGuard(FileCatalogPage, [
                  PermissionsSchema.files.fm_access,
                  PermissionsSchema.users.activate,
                ])}
              /> */}
              <Route path={STATIC_ROUTES.fileCatalog} element={<FileCatalogPage />} />
              <Route path={STATIC_ROUTES.myFiles} element={<MyFilesPage />} />
              <Route path={STATIC_ROUTES.accountSettings} element={<AccountSettings />} />
              <Route path={STATIC_ROUTES.testPage} element={<TestPage />} />
              <Route path={STATIC_ROUTES.news} element={<AddNews />} />
              <Route path={STATIC_ROUTES.newsById} element={<SingleNews />} />
              <Route path="/dashboard/projects" element={<ProjectsLayout />}>
                <Route index path={STATIC_ROUTES.projects} element={<DashboardProjects />} />
                <Route path={STATIC_ROUTES.projectId} element={<ProjectsById />} />
              </Route>
              <Route path="*" element={<NotFoundPage type={NotFoundTypes.dashboard} />} />
            </Route>
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};
export default AppRoutes;
